/**
 * Translate actions for server-rendered content.
 *
 * Markup contract: a `[data-translate-trigger]` control inside a
 * `[data-translate-scope]` element translates the scope's
 * `[data-translate-source]` text. The panel opens after the source element.
 * A selector as the source attribute value narrows the text to that
 * descendant, for example the body of a reply without its quote.
 * `data-translate-to` on the scope fixes the first target language.
 */

import { toggleTranslation } from './translation-panel.js';

function sourceText(source) {
    const selector = source.getAttribute('data-translate-source');
    const node = (selector && source.querySelector(selector)) || source;
    return (node.innerText || node.textContent || '').trim();
}

/** Nested scopes (comment replies) keep their own triggers. */
function ownTriggers(scope) {
    return [...scope.querySelectorAll('[data-translate-trigger]')]
        .filter((trigger) => trigger.closest('[data-translate-scope]') === scope);
}

function sourceOf(scope) {
    return [...scope.querySelectorAll('[data-translate-source]')]
        .find((source) => source.closest('[data-translate-scope]') === scope);
}

/**
 * The element that holds focus for the scope: a visible trigger, else the
 * menu button of the menu that holds the trigger (Twig post cards).
 */
function focusTarget(scope) {
    const own = (element) => element.closest('[data-translate-scope]') === scope;
    return ownTriggers(scope).find((trigger) => trigger.offsetParent !== null && !trigger.closest('[role="menu"]'))
        ?? [...scope.querySelectorAll('button[aria-expanded]:not([data-translate-trigger])')].find((button) => own(button) && button.offsetParent !== null);
}

function setExpanded(scope, open) {
    ownTriggers(scope).forEach((trigger) => trigger.setAttribute('aria-expanded', open ? 'true' : 'false'));
}

document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-translate-trigger]') : null;
    if (!trigger) {
        return;
    }
    event.preventDefault();
    const scope = trigger.closest('[data-translate-scope]');
    const source = scope && sourceOf(scope);
    if (source) {
        setExpanded(scope, toggleTranslation(source, sourceText(source), { to: scope.getAttribute('data-translate-to') || undefined }));
    }
    // The closing menu hides the trigger that holds focus.
    if (trigger.closest('[role="menu"]')) {
        window.dispatchEvent(new CustomEvent('close-dropdowns'));
        if (scope) {
            focusTarget(scope)?.focus();
        }
    }
});

document.addEventListener('translation-closed', (event) => {
    const scope = event.target instanceof Element ? event.target.closest('[data-translate-scope]') : null;
    if (!scope) {
        return;
    }
    setExpanded(scope, false);
    if (event.detail?.hadFocus) {
        focusTarget(scope)?.focus();
    }
});
